/**
 * Debt Eradicator — Phase 1: tracker + guided data-capture wizard.
 *
 * Empty state → 3-step wizard (pick debt types → per-debt numbers → extra + strategy).
 * Populated state → hero "Debt-free by [Month YYYY]", progress ring, per-debt cards
 * with one-tap "Log payment", and a live interest-saved comparison.
 */
import { useEffect, useMemo, useState } from "react";
import {
    CreditCard, Car, Home, Landmark, GraduationCap, Wallet,
    HandCoins, Coins, Sparkles, TrendingDown, ChevronRight, ChevronLeft, Trash2,
    Loader2, CheckCircle2, Send, AlertTriangle, Trophy, Plus,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const NZD2 = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 });

const DEBT_KINDS = [
    { key: "credit_card",   label: "Credit card / store card", icon: CreditCard, aprHint: 21.9 },
    { key: "car_loan",      label: "Car loan / vehicle finance", icon: Car, aprHint: 9.9 },
    { key: "mortgage",      label: "Mortgage",                icon: Home, aprHint: 7.5 },
    { key: "personal_loan", label: "Personal loan",           icon: Wallet, aprHint: 12.0 },
    { key: "business_loan", label: "Business loan",           icon: Landmark, aprHint: 10.0 },
    { key: "tax_debt",      label: "IRD tax debt",            icon: HandCoins, aprHint: 10.88 },
    { key: "overdraft",     label: "Bank overdraft",          icon: Coins, aprHint: 15.0 },
    { key: "student_loan",  label: "Student loan",            icon: GraduationCap, aprHint: 0.0 },
    { key: "other",         label: "Other",                   icon: Sparkles, aprHint: 8.0 },
];
const KIND_MAP = Object.fromEntries(DEBT_KINDS.map((k) => [k.key, k]));

export default function PortalDebt() {
    const { activeClientId } = useAuth();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [payDialog, setPayDialog] = useState(null);
    const [planEditing, setPlanEditing] = useState(false);

    async function load() {
        try {
            const params = activeClientId ? { client_id: activeClientId } : {};
            const { data } = await api.get("/debt", { params });
            setState(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [activeClientId]);

    if (loading) {
        return (
            <div className="flex items-center gap-3 text-[#5E636E] py-16 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading your debt tracker…
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-10" data-testid="portal-debt">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold">Debt Eradicator</h1>
                    <p className="mt-1 text-sm text-[#5E636E]">
                        Map every debt, commit an extra amount each month, and watch the freedom date get closer with every payment.
                    </p>
                </div>
                {state?.has_data && (
                    <Button
                        variant="outline"
                        onClick={() => setWizardOpen(true)}
                        data-testid="add-debt-btn"
                    >
                        <Plus className="h-4 w-4" /> Add debt
                    </Button>
                )}
            </div>

            {!state?.has_data ? (
                <EmptyState onStart={() => setWizardOpen(true)} />
            ) : (
                <>
                    <FreedomHero state={state} onEditPlan={() => setPlanEditing(true)} />
                    <DebtsList state={state} onLogPayment={setPayDialog} onReload={load} />
                    <ProgressCard state={state} />
                    <RecentPayments state={state} />
                </>
            )}

            <DebtWizardDialog
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onDone={() => { setWizardOpen(false); load(); }}
                existingCount={state?.totals?.count || 0}
            />

            <PayDialog
                debt={payDialog}
                onClose={() => setPayDialog(null)}
                onDone={() => { setPayDialog(null); load(); }}
            />

            <PlanEditDialog
                open={planEditing}
                initial={state?.plan}
                onClose={() => setPlanEditing(false)}
                onSaved={() => { setPlanEditing(false); load(); }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// Empty state — the "first-time-user" moment
// ---------------------------------------------------------------------------
function EmptyState({ onStart }) {
    return (
        <div className="rounded-2xl border border-[#E4E4E7] bg-gradient-to-br from-emerald-50 to-white p-8 sm:p-12 text-center" data-testid="debt-empty-state">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 grid place-items-center">
                <TrendingDown className="h-7 w-7 text-emerald-700" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-semibold">Let&apos;s build your fastest path out of debt.</h2>
            <p className="mt-2 text-sm text-[#5E636E] max-w-md mx-auto">
                Answer three quick questions and we&apos;ll show you exactly when you&apos;ll be debt-free — plus how much interest you keep in your own pocket by sticking to the plan.
            </p>
            <Button
                onClick={onStart}
                className="mt-6 bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                data-testid="start-debt-wizard-btn"
            >
                <Sparkles className="h-4 w-4" /> Start my free debt check
            </Button>
            <p className="mt-3 text-[11px] text-[#5E636E]">All numbers stay private to you and your accountant.</p>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Freedom hero — the daily-check surface
// ---------------------------------------------------------------------------
function FreedomHero({ state, onEditPlan }) {
    const proj = state.projection;
    const cmp = state.comparison;
    const impossible = proj?.payoff_impossible;
    const monthsLabel = proj.months_to_freedom
        ? `${proj.months_to_freedom} month${proj.months_to_freedom === 1 ? "" : "s"} from today`
        : "You need to raise the extra payment for this to work";

    return (
        <div className="rounded-2xl border border-[#0E3B2E]/20 bg-gradient-to-br from-emerald-950 to-emerald-700 text-white p-5 sm:p-6" data-testid="freedom-hero">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-wider text-emerald-100/80">You&apos;ll be debt-free</div>
                    <div className="mt-1 font-display text-3xl sm:text-4xl font-semibold" data-testid="freedom-date">
                        {proj.freedom_date ? niceMonth(proj.freedom_date) : "—"}
                    </div>
                    <div className="mt-1 text-sm text-white/85" data-testid="freedom-months">{monthsLabel}</div>
                </div>
                <button
                    type="button"
                    onClick={onEditPlan}
                    className="rounded-md border border-white/25 bg-white/10 hover:bg-white/20 px-3 py-2 text-xs"
                    data-testid="edit-plan-btn"
                >
                    Extra {NZD.format(state.plan?.monthly_extra || 0)}/mo · {state.plan?.strategy || "avalanche"}
                    <span className="ml-1 opacity-70">edit</span>
                </button>
            </div>
            {impossible && (
                <div className="mt-4 rounded-md bg-amber-100/95 text-amber-900 px-3 py-2 text-sm flex items-start gap-2" data-testid="payoff-impossible-warning">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                        At the current minimums + extra amount, at least one debt&apos;s interest grows faster than you can pay it down. Bump the monthly extra or the minimums to fix this.
                    </span>
                </div>
            )}
            {!impossible && cmp?.months_saved > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4">
                    <MiniStat label="Interest kept in your pocket" value={NZD.format(cmp.interest_saved)} />
                    <MiniStat label="Months you save vs minimums" value={cmp.months_saved} />
                </div>
            )}
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="rounded-lg bg-white/10 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-white/70">{label}</div>
            <div className="mt-0.5 font-display text-lg font-semibold">{value}</div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Debts list — per-debt cards with "Log payment"
// ---------------------------------------------------------------------------
function DebtsList({ state, onLogPayment, onReload }) {
    const proj = state.projection;
    const projMap = useMemo(() => Object.fromEntries(proj.per_debt.map((p) => [p.id, p])), [proj]);

    async function archive(debt) {
        if (!window.confirm(`Remove "${debt.name}" from tracking? (Payments history is kept.)`)) return;
        try {
            await api.delete(`/debt/debts/${debt.id}`);
            toast.success(`${debt.name} removed`);
            onReload();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <section>
            <h2 className="font-display text-lg font-semibold mb-2">What you&apos;re knocking out</h2>
            <div className="grid gap-2" data-testid="debt-list">
                {state.debts.map((d) => {
                    const p = projMap[d.id] || {};
                    const startBal = d.starting_balance || d.balance;
                    const paidDown = Math.max(0, startBal - d.balance);
                    const pct = startBal > 0 ? Math.min(100, Math.round((paidDown / startBal) * 100)) : 0;
                    const K = KIND_MAP[d.kind] || KIND_MAP.other;
                    const Icon = K.icon;
                    return (
                        <div key={d.id} className="rounded-lg border border-[#E4E4E7] bg-white p-3 sm:p-4" data-testid={`debt-card-${d.id}`}>
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center shrink-0">
                                    <Icon className="h-5 w-5 text-emerald-700" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                        <div className="font-medium truncate">{d.name}</div>
                                        <div className="font-display text-lg font-semibold" data-testid={`debt-balance-${d.id}`}>{NZD2.format(d.balance)}</div>
                                    </div>
                                    <div className="mt-0.5 text-[11px] text-[#5E636E]">
                                        {K.label} · {d.apr}% APR · min {NZD2.format(d.minimum_payment)}/mo
                                    </div>
                                    <div className="mt-2 h-1.5 rounded-full bg-[#F1F1F3] overflow-hidden">
                                        <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="mt-1 flex items-center justify-between text-[11px] text-[#5E636E]">
                                        <span>{pct}% paid down from {NZD.format(startBal)}</span>
                                        {p.months_to_payoff && (
                                            <span className="text-emerald-700">
                                                Gone in {p.months_to_payoff} month{p.months_to_payoff === 1 ? "" : "s"}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2 justify-end">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => archive(d)}
                                    className="text-[#5E636E] hover:text-[#B91C1C] text-xs"
                                    data-testid={`archive-debt-${d.id}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" /> Remove
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => onLogPayment(d)}
                                    className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                                    data-testid={`log-payment-btn-${d.id}`}
                                >
                                    <Send className="h-3.5 w-3.5" /> Log payment
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Progress card + recent payments
// ---------------------------------------------------------------------------
function ProgressCard({ state }) {
    const t = state.totals;
    return (
        <section className="rounded-lg border border-[#E4E4E7] bg-white p-4" data-testid="debt-progress">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <div className="font-display text-base font-semibold">How far you&apos;ve come</div>
                <div className="text-[11px] text-[#5E636E]">{NZD.format(t.paid_down)} paid down of {NZD.format(t.starting_balance)}</div>
            </div>
            <div className="h-3 rounded-full bg-[#F1F1F3] overflow-hidden">
                <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-emerald-700 transition-all duration-500"
                    style={{ width: `${Math.max(2, Math.min(100, t.progress_pct))}%` }}
                    data-testid="debt-progress-bar"
                />
            </div>
            <div className="mt-1.5 text-xs text-[#5E636E] flex items-center justify-between">
                <span>{t.progress_pct}% eliminated</span>
                <span className="text-emerald-700 font-medium">{NZD.format(t.current_balance)} to go</span>
            </div>
        </section>
    );
}

function RecentPayments({ state }) {
    if (!state.recent_payments?.length) return null;
    return (
        <section>
            <h2 className="font-display text-base font-semibold mb-2">Recent payments</h2>
            <div className="rounded-lg border border-[#E4E4E7] bg-white divide-y divide-[#F1F1F3]" data-testid="debt-payment-history">
                {state.recent_payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                        <div className="min-w-0">
                            <div className="font-medium truncate">{p.debt_name}</div>
                            <div className="text-[11px] text-[#5E636E]">{niceDate(p.paid_at)}{p.note ? ` · ${p.note}` : ""}</div>
                        </div>
                        <div className="text-right">
                            <div className="font-semibold text-emerald-700">{NZD2.format(p.amount)}</div>
                            {p.balance_after === 0 && (
                                <div className="text-[10px] text-emerald-700 flex items-center gap-1 justify-end">
                                    <Trophy className="h-3 w-3" /> PAID OFF
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Wizard — 3 steps
// ---------------------------------------------------------------------------
function DebtWizardDialog({ open, onClose, onDone, existingCount }) {
    const [step, setStep] = useState(1);
    const [picked, setPicked] = useState([]);
    const [debts, setDebts] = useState([]);
    const [extra, setExtra] = useState("");
    const [strategy, setStrategy] = useState("avalanche");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setStep(1);
            setPicked([]);
            setDebts([]);
            setExtra("");
            setStrategy("avalanche");
        }
    }, [open]);

    function togglePick(kind) {
        setPicked((p) => (p.includes(kind) ? p.filter((k) => k !== kind) : [...p, kind]));
    }

    function next() {
        if (step === 1) {
            // Seed debts array with one row per picked kind, with hint values
            const seeded = picked.map((kind) => {
                const k = KIND_MAP[kind];
                return {
                    kind,
                    name: k.label,
                    balance: "",
                    apr: k.aprHint,
                    minimum_payment: "",
                };
            });
            setDebts(seeded);
            setStep(2);
        } else if (step === 2) {
            setStep(3);
        }
    }

    async function submit() {
        setBusy(true);
        try {
            const rows = debts
                .map((d) => ({
                    kind: d.kind,
                    name: (d.name || KIND_MAP[d.kind]?.label || "Debt").trim(),
                    balance: Number(d.balance) || 0,
                    apr: Number(d.apr) || 0,
                    minimum_payment: Number(d.minimum_payment) || 0,
                }))
                .filter((d) => d.balance > 0);
            if (!rows.length) {
                toast.error("Add at least one debt with a balance greater than $0.");
                setBusy(false);
                return;
            }
            await api.post("/debt/debts/bulk", { debts: rows });
            await api.post("/debt/plan", {
                monthly_extra: Number(extra) || 0,
                strategy,
            });
            toast.success(`Nice — ${rows.length} debt${rows.length === 1 ? "" : "s"} mapped. Let&apos;s knock &apos;em out.`);
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const totalMinimums = debts.reduce((sum, d) => sum + (Number(d.minimum_payment) || 0), 0);
    const totalBalance = debts.reduce((sum, d) => sum + (Number(d.balance) || 0), 0);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="debt-wizard">
                <DialogHeader>
                    <DialogTitle>
                        {existingCount > 0 ? "Add more debts" : "Let\u2019s map your debt"}
                        <span className="ml-2 text-xs text-[#5E636E] font-normal">Step {step} of 3</span>
                    </DialogTitle>
                    <DialogDescription>
                        {step === 1 && "Tick every debt type you currently owe on."}
                        {step === 2 && "For each debt, tell us the balance, interest rate, and minimum monthly payment."}
                        {step === 3 && "How much extra can you commit each month, and which order do you want to attack them in?"}
                    </DialogDescription>
                </DialogHeader>

                {step === 1 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wizard-step-1">
                        {DEBT_KINDS.map((k) => {
                            const active = picked.includes(k.key);
                            const Icon = k.icon;
                            return (
                                <button
                                    key={k.key}
                                    type="button"
                                    onClick={() => togglePick(k.key)}
                                    className={`rounded-lg border p-3 text-left text-sm transition-colors ${active ? "border-emerald-600 bg-emerald-50/70 ring-2 ring-emerald-200" : "border-[#E4E4E7] hover:bg-zinc-50"}`}
                                    data-testid={`wizard-kind-${k.key}`}
                                >
                                    <Icon className={`h-4 w-4 mb-1 ${active ? "text-emerald-700" : "text-[#5E636E]"}`} />
                                    <div className="text-xs font-medium leading-tight">{k.label}</div>
                                    {active && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-1" />}
                                </button>
                            );
                        })}
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-3 flex-1 overflow-y-auto" data-testid="wizard-step-2">
                        {debts.map((d, i) => {
                            const K = KIND_MAP[d.kind];
                            const Icon = K.icon;
                            return (
                                <div key={i} className="rounded-lg border border-[#E4E4E7] p-3" data-testid={`wizard-debt-row-${d.kind}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Icon className="h-4 w-4 text-emerald-700" />
                                        <Input
                                            value={d.name}
                                            onChange={(e) => setDebts(debts.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                                            className="flex-1 h-8 text-sm"
                                            placeholder="Nickname (e.g. ANZ Visa)"
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <NumberField label="Balance $" value={d.balance} onChange={(v) => setDebts(debts.map((x, j) => j === i ? { ...x, balance: v } : x))} testId={`wizard-balance-${d.kind}`} />
                                        <NumberField label="APR %" value={d.apr} onChange={(v) => setDebts(debts.map((x, j) => j === i ? { ...x, apr: v } : x))} testId={`wizard-apr-${d.kind}`} />
                                        <NumberField label="Min /mo $" value={d.minimum_payment} onChange={(v) => setDebts(debts.map((x, j) => j === i ? { ...x, minimum_payment: v } : x))} testId={`wizard-min-${d.kind}`} />
                                    </div>
                                </div>
                            );
                        })}
                        <div className="text-[11px] text-[#5E636E]">
                            APR tips are rough NZ averages. Grab the exact number from your bank statement if you have it.
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-4" data-testid="wizard-step-3">
                        <div className="rounded-md border border-[#E4E4E7] bg-[#F8F8F5] px-3 py-2 text-sm">
                            You&apos;re about to track <strong>{debts.length}</strong> debt{debts.length === 1 ? "" : "s"} totalling <strong>{NZD.format(totalBalance)}</strong>. Minimums add up to <strong>{NZD.format(totalMinimums)}/mo</strong>.
                        </div>
                        <div>
                            <Label htmlFor="wiz-extra">Extra you can put on debt each month</Label>
                            <Input
                                id="wiz-extra"
                                type="number"
                                inputMode="decimal"
                                min={0}
                                step={10}
                                placeholder="e.g. 400"
                                value={extra}
                                onChange={(e) => setExtra(e.target.value)}
                                className="mt-1"
                                data-testid="wizard-extra-input"
                            />
                            <p className="mt-1 text-[11px] text-[#5E636E]">
                                Even $50/mo extra can shave years off. Start small — you can adjust anytime.
                            </p>
                        </div>
                        <div>
                            <Label>Which debt should the extra hit first?</Label>
                            <div className="mt-1 grid grid-cols-2 gap-2">
                                <StrategyPick
                                    active={strategy === "avalanche"}
                                    onClick={() => setStrategy("avalanche")}
                                    title="Avalanche"
                                    hint="Highest APR first — saves the most interest overall (usually the cheapest path)"
                                    testId="strategy-avalanche"
                                />
                                <StrategyPick
                                    active={strategy === "snowball"}
                                    onClick={() => setStrategy("snowball")}
                                    title="Snowball"
                                    hint="Smallest balance first — gives you quick wins, best for motivation"
                                    testId="strategy-snowball"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {step > 1 && (
                        <Button variant="outline" onClick={() => setStep(step - 1)} disabled={busy} data-testid="wizard-back">
                            <ChevronLeft className="h-4 w-4" /> Back
                        </Button>
                    )}
                    {step < 3 ? (
                        <Button
                            onClick={next}
                            disabled={step === 1 && picked.length === 0}
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            data-testid="wizard-next"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Button
                            onClick={submit}
                            disabled={busy}
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            data-testid="wizard-finish"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4" /> Save & see my freedom date</>}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function NumberField({ label, value, onChange, testId }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#5E636E]">{label}</Label>
            <Input
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-0.5 h-8 text-sm"
                data-testid={testId}
            />
        </div>
    );
}

function StrategyPick({ active, onClick, title, hint, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-lg border p-3 text-left transition-colors ${active ? "border-emerald-600 bg-emerald-50/70 ring-2 ring-emerald-200" : "border-[#E4E4E7] hover:bg-zinc-50"}`}
            data-testid={testId}
        >
            <div className="font-medium text-sm">{title}</div>
            <div className="mt-0.5 text-[11px] text-[#5E636E] leading-relaxed">{hint}</div>
        </button>
    );
}

// ---------------------------------------------------------------------------
// Payment & plan-edit dialogs
// ---------------------------------------------------------------------------
function PayDialog({ debt, onClose, onDone }) {
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (debt) { setAmount(debt.minimum_payment ? String(debt.minimum_payment) : ""); setNote(""); }
    }, [debt?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!debt) return null;
    async function submit() {
        setBusy(true);
        try {
            const { data } = await api.post(`/debt/debts/${debt.id}/payment`, {
                amount: Number(amount),
                note: note.trim() || undefined,
            });
            const paidOff = data.payment.balance_after === 0;
            if (paidOff) toast.success(`🏆 ${debt.name} is PAID OFF. Legend.`);
            else toast.success(`Logged ${NZD2.format(data.payment.amount)} against ${debt.name}. New balance ${NZD2.format(data.payment.balance_after)}.`);
            onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    return (
        <Dialog open={!!debt} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="pay-dialog">
                <DialogHeader>
                    <DialogTitle>Log a payment</DialogTitle>
                    <DialogDescription>
                        Current balance on <strong>{debt.name}</strong>: {NZD2.format(debt.balance)}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label htmlFor="pay-amount">Amount paid</Label>
                        <Input
                            id="pay-amount"
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            data-testid="pay-amount-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="pay-note">Note (optional)</Label>
                        <Input
                            id="pay-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="e.g. Bonus payment, tax refund"
                            data-testid="pay-note-input"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || !amount || Number(amount) <= 0}
                        className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                        data-testid="pay-confirm-btn"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function PlanEditDialog({ open, initial, onClose, onSaved }) {
    const [extra, setExtra] = useState("");
    const [strategy, setStrategy] = useState("avalanche");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && initial) {
            setExtra(String(initial.monthly_extra ?? 0));
            setStrategy(initial.strategy || "avalanche");
        }
    }, [open, initial]);

    async function save() {
        setBusy(true);
        try {
            await api.post("/debt/plan", { monthly_extra: Number(extra) || 0, strategy });
            toast.success("Plan updated");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="plan-edit-dialog">
                <DialogHeader>
                    <DialogTitle>Adjust your payoff plan</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Extra per month</Label>
                        <Input
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step={10}
                            value={extra}
                            onChange={(e) => setExtra(e.target.value)}
                            data-testid="plan-extra-input"
                        />
                    </div>
                    <div>
                        <Label>Strategy</Label>
                        <Select value={strategy} onValueChange={setStrategy}>
                            <SelectTrigger data-testid="plan-strategy-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="avalanche">Avalanche (highest APR first) — saves most interest</SelectItem>
                                <SelectItem value="snowball">Snowball (smallest balance first) — quick wins</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={save} disabled={busy} className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white" data-testid="plan-save-btn">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------
function niceMonth(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-NZ", { year: "numeric", month: "long" });
    } catch { return iso; }
}
function niceDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
}
